/**
 * Custom react-admin notification (#4265 — cleaner, more informative toasts).
 *
 * Replaces react-admin's single-at-a-time `<Notification>` with a bottom-right
 * STACK, and adds:
 *  - a thin progress bar that drains over the auto-hide duration and pauses on
 *    hover, so a toast won't disappear while you read or copy it. Errors get a
 *    longer duration than success/info (set by the caller) instead of being
 *    sticky; a caller can still pin a toast with an explicit `null` duration;
 *  - structured rendering of our rich errors (title + actionable details +
 *    code/correlation-id), passed through the notify `_aegle` option;
 *  - a copy button, shown only when there's something worth copying (a `code` or
 *    a correlation id), copying the full formatted error for support;
 *  - per-toast slide in/out from the right.
 *
 * react-admin's own notifications (created/updated, and the undoable
 * "deleted / UNDO" toast) render through the same shell; undoable commit/undo is
 * preserved via `useTakeUndoableMutation`.
 */
import { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import {
  Box,
  Paper,
  Alert,
  AlertTitle,
  IconButton,
  Button,
  Typography,
  useTheme,
  type AlertColor,
} from "@mui/material";
import { keyframes } from "@mui/system";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useNotificationContext, useTakeUndoableMutation, useTranslate } from "react-admin";
import { formatRichErrorMessage, type RichError } from "@/utils/errors/richError";

const DEFAULT_AUTO_HIDE = 5000;
const MAX_VISIBLE = 5;
const LEAVE_MS = 240;

const slideIn = keyframes({
  from: { opacity: 0, transform: "translateX(28px)" },
  to: { opacity: 1, transform: "translateX(0)" },
});
const slideOut = keyframes({
  from: { opacity: 1, transform: "translateX(0)" },
  to: { opacity: 0, transform: "translateX(28px)" },
});
const drain = keyframes({ from: { transform: "scaleX(1)" }, to: { transform: "scaleX(0)" } });

const toSeverity = (type?: string): AlertColor =>
  type === "success" || type === "error" || type === "warning" || type === "info" ? type : "info";

type NotificationPayload = ReturnType<typeof useNotificationContext>["notifications"][number];

interface ActiveToast {
  id: number;
  payload: NotificationPayload;
}

// Identity of a toast for de-duplication: same severity + message + error code/detail.
// (Correlation id is excluded — it can differ across retries of the same failure.)
const toastSignature = (payload: NotificationPayload): string => {
  const rich = (payload.notificationOptions as { _aegle?: RichError } | undefined)?._aegle;
  const message = typeof payload.message === "string" ? payload.message : "";
  return `${payload.type ?? ""}|${message}|${rich?.code ?? ""}|${rich?.details ?? ""}`;
};

interface ToastCardProps {
  payload: NotificationPayload;
  title: ReactNode;
  onDismiss: () => void;
  onUndo: () => void;
}

const ToastCard = ({ payload, title, onDismiss, onUndo }: ToastCardProps) => {
  const theme = useTheme();
  const { type, notificationOptions } = payload;
  const opts = (notificationOptions ?? {}) as {
    autoHideDuration?: number | null;
    multiLine?: boolean;
    undoable?: boolean;
    _aegle?: RichError;
  };

  const severity = toSeverity(type);
  const rich = opts._aegle;
  const undoable = Boolean(opts.undoable);
  // Everything auto-hides with the draining bar; hovering pauses it so a toast
  // won't vanish while you read or copy. Errors get a longer duration (set by the
  // caller) rather than being sticky. Only an explicit `null` duration stays up.
  const sticky = opts.autoHideDuration === null;
  const durationMs = opts.autoHideDuration ?? DEFAULT_AUTO_HIDE;

  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const leavingRef = useRef(false);
  const remainingRef = useRef(durationMs);

  const barColor = theme.palette[severity]?.main ?? theme.palette.info.main;
  const showCopy = Boolean(rich && (rich.code || rich.correlationId));

  const beginLeave = useCallback(
    (undo: boolean) => {
      if (leavingRef.current) return;
      leavingRef.current = true;
      setLeaving(true);
      window.setTimeout(() => (undo ? onUndo() : onDismiss()), LEAVE_MS);
    },
    [onDismiss, onUndo]
  );

  // Auto-hide timer for non-sticky toasts, paused while hovered (remaining time preserved).
  useEffect(() => {
    if (sticky || leaving || hovered) return;
    const started = Date.now();
    const timer = window.setTimeout(() => beginLeave(false), remainingRef.current);
    return () => {
      window.clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - started));
    };
  }, [sticky, leaving, hovered, beginLeave]);

  const handleCopy = () => {
    const text = rich ? formatRichErrorMessage(rich) : String(title ?? "");
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, done);
    } else {
      done();
    }
  };

  return (
    <Paper
      elevation={6}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 2.5,
        minWidth: 300,
        maxWidth: 360,
        pointerEvents: "auto",
        animation: `${leaving ? slideOut : slideIn} ${leaving ? LEAVE_MS : 300}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
      }}
    >
      {!sticky ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            height: 2,
            width: "100%",
            transformOrigin: "left",
            backgroundColor: barColor,
            animation: `${drain} ${durationMs}ms linear forwards`,
            animationPlayState: hovered ? "paused" : "running",
          }}
        />
      ) : null}

      <Alert
        severity={severity}
        variant="standard"
        sx={{
          alignItems: "flex-start",
          py: 0.75,
          px: 1.25,
          whiteSpace: opts.multiLine ? "pre-wrap" : "normal",
          "& .MuiAlert-message": { width: "100%", py: 0.25 },
          "& .MuiAlert-icon": { py: 0, mr: 1, fontSize: 20, alignItems: "flex-start", mt: "2px" },
          "& .MuiAlert-action": { pt: 0, pl: 0.5, mr: 0 },
        }}
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {undoable ? (
              <Button color="inherit" size="small" onClick={() => beginLeave(true)}>
                Undo
              </Button>
            ) : null}
            {showCopy ? (
              <IconButton size="small" color="inherit" onClick={handleCopy} aria-label="Copy error details">
                {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            ) : null}
            <IconButton size="small" color="inherit" onClick={() => beginLeave(false)} aria-label="Dismiss">
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        }
      >
        {rich ? (
          <>
            <AlertTitle sx={{ fontSize: 13.5, fontWeight: 500, mt: 0, mb: rich.details ? 0.25 : 0 }}>
              {title}
            </AlertTitle>
            {rich.details ? (
              <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: "text.secondary" }}>{rich.details}</Typography>
            ) : null}
            {rich.code || rich.correlationId ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.75, flexWrap: "wrap" }}>
                {rich.code ? (
                  <Box
                    component="code"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 11,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      backgroundColor: "action.hover",
                    }}
                  >
                    {rich.code}
                  </Box>
                ) : null}
                {rich.correlationId ? (
                  <Typography variant="caption" sx={{ fontSize: 11, color: "text.disabled" }}>
                    ref {rich.correlationId}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </>
        ) : (
          <Box sx={{ fontSize: 13.5 }}>{title}</Box>
        )}
      </Alert>
    </Paper>
  );
};

/**
 * Drop-in for `<Admin notification={AegleNotification} />`. Drains react-admin's
 * notification queue into a bottom-right stack (max {@link MAX_VISIBLE} visible;
 * overflow waits in the queue until a slot frees).
 */
export const AegleNotification = () => {
  const { notifications, takeNotification } = useNotificationContext();
  const takeMutation = useTakeUndoableMutation();
  const translate = useTranslate();
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const idRef = useRef(0);

  useEffect(() => {
    // Take exactly ONE notification per run. `takeNotification()` returns
    // `notifications[0]` from its closure and removes it via an async setState, so
    // calling it repeatedly in a synchronous loop would return the SAME item every
    // time (which is what produced the duplicate/hang bug). Instead we drain
    // one-at-a-time and let the effect re-run — it depends on `notifications` (which
    // changes as items are taken) and on `toasts` (so freeing a slot pulls the next).
    if (notifications.length > 0 && toasts.length < MAX_VISIBLE) {
      // Show undoable toasts strictly one at a time. ra-core's undoable mutations are
      // a FIFO queue (takeMutation shifts the head), so a second concurrent undoable
      // toast would map Undo/dismiss to the WRONG mutation. If an undoable toast is
      // already displayed, leave the next one queued until it resolves. Non-undoable
      // toasts still stack freely.
      const headUndoable = Boolean(notifications[0]?.notificationOptions?.undoable);
      const undoableShowing = toasts.some((t) => t.payload.notificationOptions?.undoable);
      if (!(headUndoable && undoableShowing)) {
        const payload = takeNotification();
        if (payload) {
          const undoable = Boolean(payload.notificationOptions?.undoable);
          const sig = toastSignature(payload);
          // Collapse a duplicate already on screen (a retried request, or several
          // components / checkError all notifying the same failure). Never dedup an
          // undoable toast (distinct pending mutation) or a non-string message (JSX
          // element messages aren't comparable by signature).
          const isDuplicate =
            !undoable && typeof payload.message === "string" && toasts.some((t) => toastSignature(t.payload) === sig);
          if (!isDuplicate) {
            setToasts((prev) => [...prev, { id: (idRef.current += 1), payload }]);
          }
        }
      }
    }
    // Warn before unload while an undoable mutation is still pending. Key off the
    // DISPLAYED toasts (the queue is drained into `toasts` within one render, so
    // checking `notifications` alone would miss the whole undo window), plus the
    // queue for anything not yet displayed.
    const undoablePending =
      toasts.some((t) => t.payload.notificationOptions?.undoable) ||
      notifications.some((n) => n?.notificationOptions?.undoable);
    if (undoablePending) {
      const beforeunload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "";
        return "";
      };
      window.addEventListener("beforeunload", beforeunload);
      return () => window.removeEventListener("beforeunload", beforeunload);
    }
  }, [notifications, takeNotification, toasts]);

  const remove = useCallback((id: number) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);

  const dismiss = useCallback(
    (toast: ActiveToast) => {
      // Dismissing an undoable toast commits the pending mutation (matches react-admin).
      if (toast.payload.notificationOptions?.undoable) {
        const mutation = takeMutation();
        if (mutation) mutation({ isUndo: false });
      }
      remove(toast.id);
    },
    [takeMutation, remove]
  );

  const undo = useCallback(
    (toast: ActiveToast) => {
      const mutation = takeMutation();
      if (mutation) mutation({ isUndo: true });
      remove(toast.id);
    },
    [takeMutation, remove]
  );

  if (!toasts.length) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: (t) => t.zIndex.snackbar,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        pointerEvents: "none",
      }}
    >
      {toasts.map((toast) => {
        const { message, notificationOptions } = toast.payload;
        const messageArgs = (notificationOptions as { messageArgs?: Record<string, unknown> })?.messageArgs;
        const title = typeof message === "string" ? translate(message, messageArgs) : message;
        return (
          <ToastCard
            key={toast.id}
            payload={toast.payload}
            title={title}
            onDismiss={() => dismiss(toast)}
            onUndo={() => undo(toast)}
          />
        );
      })}
    </Box>
  );
};

export default AegleNotification;
